import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { cn, formatCompactOverdue, formatToISODuration } from "@/lib/utils";
import { Button, ButtonProps } from "@/components/Base/Button";
import { PauseIcon, PlayIcon, SquareIcon, TimerResetIcon } from "lucide-react";
import { Spinner } from "@/components/Common/Spinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Base/Popover";
import { Timer } from "@/types/v2";
import { serverNow } from "@/lib/serverClock";
import { useTickerStore } from "@/stores/useTickerStore";
import { ClassNameValue } from "tailwind-merge";

export interface CardTimerProps {
  onStart?: () => void;
  onStop?: () => void;
  onReset?: () => void;
  onPause?: (accumulatedTime: number) => void;
  startTime?: number | Date;
  accumulatedTime?: number;
  limit?: number;
  stopComponent?: ReactNode;
  loading?: boolean;
  callback?: (accumulatedTime: number, state: Timer["state"]) => void;
  state?: "running" | "paused" | "stopped";
  disabled?: boolean;
  className?: ClassNameValue;
  timerClassName?: ClassNameValue;
  timerSecondsClassName?: ClassNameValue;
  iconSize?: number;
  triggerSize?: ButtonProps["size"];
  /**
   * Footer-density rendering: an overdue timer collapses to "+Nh" once it is an
   * hour or more past its limit, and the root may shrink and is clipped
   * (overflow-hidden), so the job-hours pill beside it is never the one to give
   * up room. Off by default so the list view and stories keep the full
   * "+h:mm:ss".
   */
  compact?: boolean;
}
export const CardTimer = ({
  onStart,
  onStop,
  onReset,
  onPause,
  startTime,
  accumulatedTime,
  limit,
  stopComponent,
  loading,
  callback,
  state,
  disabled,
  className,
  timerClassName,
  timerSecondsClassName,
  iconSize: propIconSize,
  triggerSize = "tinyIcon",
  compact,
}: CardTimerProps) => {
  const { ticker } = useTickerStore();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [startingTime, setStartingTime] = useState<Date>(
    startTime && startTime instanceof Date
      ? startTime
      : startTime
        ? new Date(startTime)
        : new Date(serverNow())
  );

  const [accumulatingTime, setAccumulatingTime] = useState<number>(
    accumulatedTime ?? 0
  );
  const [currentTime, setCurrentTime] = useState<Date>(new Date(serverNow()));
  // Clamped at 0: a start time slightly ahead of this device's clock must
  // never produce negative elapsed time.
  const elapsedTime = useMemo(
    () =>
      Math.max(0, currentTime.getTime() - startingTime.getTime()) +
      (accumulatingTime ?? 0),
    [currentTime, startingTime, accumulatingTime]
  );

  const [isStarted, setIsStarted] = useState(false);

  const timeRemaining = useMemo(
    () => (limit ? limit - elapsedTime : undefined),
    [elapsedTime, limit]
  );

  useEffect(() => {
    if (startTime && state === "running") {
      setIsStarted(true);
      setStartingTime(
        startTime && startTime instanceof Date
          ? startTime
          : startTime
            ? new Date(startTime)
            : new Date(serverNow())
      );
      setAccumulatingTime(accumulatedTime ?? 0);
    }
    if (state === "paused") {
      setIsStarted(false);
      setAccumulatingTime(accumulatedTime ?? 0);
      setStartingTime(
        startTime && startTime instanceof Date
          ? startTime
          : startTime
            ? new Date(startTime)
            : new Date(serverNow())
      );
    }
    if (state === "stopped") {
      setIsStarted(false);
      setAccumulatingTime(0);
    }
  }, [state, startTime, accumulatedTime]);

  useEffect(() => {
    if (isStarted) {
      const now = serverNow();
      setCurrentTime(new Date(now));
      if (callback) {
        callback(
          accumulatingTime + Math.max(0, now - startingTime.getTime()),
          "running"
        );
      }
    } else {
      if (callback) {
        callback(accumulatingTime, accumulatingTime > 0 ? "paused" : "stopped");
      }
    }
  }, [ticker, isStarted, callback, accumulatingTime, startingTime]);

  const handleStop = () => {
    setPopoverOpen(false);
    setIsStarted(false);
    handleReset();
    if (onStop) {
      onStop();
    }
  };

  const handleStart = () => {
    setPopoverOpen(false);
    const now = new Date(serverNow());
    setStartingTime(now);
    setCurrentTime(now);
    setIsStarted(true);
    if (onStart) {
      onStart();
    }
  };

  const handlePause = () => {
    setPopoverOpen(false);
    setIsStarted(false);
    const updatedAccumulatedTime =
      accumulatingTime +
      Math.max(0, currentTime.getTime() - startingTime.getTime());
    setStartingTime(new Date(serverNow()));
    setAccumulatingTime(updatedAccumulatedTime);
    if (onPause) {
      onPause(updatedAccumulatedTime);
    }
  };

  const handleReset = () => {
    setPopoverOpen(false);
    setAccumulatingTime(0);
    if (onReset) {
      onReset();
    }
    if (callback) {
      callback(0, "stopped");
    }
  };

  const timerValue = formatToISODuration(
    timeRemaining ? Math.abs(timeRemaining) : elapsedTime
  );

  const isOverdue = timeRemaining !== undefined && timeRemaining < 0;

  const iconSize = propIconSize || 10;
  const popoverIconSize = propIconSize || 14;

  return (
    <div
      data-footer-timer={compact ? "" : undefined}
      className={cn(
        "flex items-center justify-between gap-[1px] self-center p-0",
        compact && "min-w-0 overflow-hidden"
      )}
    >
      {isStarted || accumulatingTime > 0 ? (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              disabled={disabled}
              size={triggerSize}
              variant={
                isStarted || accumulatingTime > 0
                  ? "primary-alt"
                  : "success-alt"
              }
              className={cn(
                "rounded-sm stroke-primary hover:stroke-primary-foreground",
                isStarted || accumulatingTime > 0
                  ? compact
                    ? // justify-start (not Button's default justify-center): when the
                      // root is squeezed the text overflows to the RIGHT only, so the
                      // tail is clipped, not both ends, and the root's scrollWidth
                      // is the honest natural width that useCompactNotes reads. With
                      // centering the left half of the overflow is unreachable and
                      // scrollWidth reports only about half of what is clipped.
                      "h-4 w-full justify-start px-[3px]"
                    : "h-4 w-full px-1"
                  : triggerSize !== "tinyIcon"
                    ? ""
                    : "w-4",
                className
              )}
              onClick={() => setPopoverOpen(!popoverOpen)}
            >
              {isStarted || accumulatingTime > 0 ? (
                compact && isOverdue ? (
                  <div className={cn("text-2xs", timerClassName)}>
                    {formatCompactOverdue(Math.abs(timeRemaining ?? 0))}
                  </div>
                ) : (
                  <div className={cn("text-2xs", timerClassName)}>
                    {`${timeRemaining && timeRemaining < 0 ? "+" : ""}`}
                    {(timerValue.hours !== "0" ? timerValue.hours + ":" : "") +
                      timerValue.minutes +
                      ":"}
                    <span
                      className={cn(
                        timerValue.hours !== "0" ? "text-3xs" : "text-2xs",
                        timerSecondsClassName
                      )}
                    >
                      {timerValue.seconds}
                    </span>
                  </div>
                )
              ) : (
                <PlayIcon size={iconSize} />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex flex-row gap-1 p-1">
            {isStarted ? (
              <Fragment>
                <Button
                  disabled={disabled}
                  size={"smIcon"}
                  variant={"warning"}
                  onClick={handlePause}
                >
                  <PauseIcon size={popoverIconSize} />
                </Button>
                <Button
                  size={"smIcon"}
                  variant={"destructive"}
                  onClick={handleStop}
                >
                  <SquareIcon size={popoverIconSize} />
                </Button>
              </Fragment>
            ) : null}
            {accumulatingTime > 0 && !isStarted ? (
              <Fragment>
                <Button
                  size={"smIcon"}
                  onClick={handleStart}
                  variant={"success-alt"}
                >
                  <PlayIcon size={popoverIconSize} />
                </Button>
                <Button
                  size={"smIcon"}
                  onClick={handleReset}
                  variant={"primary-alt"}
                >
                  <TimerResetIcon size={popoverIconSize} />
                </Button>
              </Fragment>
            ) : null}
          </PopoverContent>
        </Popover>
      ) : (
        <Button
          disabled={loading || disabled}
          size={triggerSize}
          className={cn("h-4 w-4 rounded-sm", className)}
          onClick={isStarted ? handleStop : handleStart}
          variant={isStarted ? "destructive-alt" : "success-alt"}
        >
          {isStarted ? (
            <SquareIcon size={iconSize} />
          ) : (
            <PlayIcon size={iconSize} />
          )}
        </Button>
      )}
      {loading ? (
        <Spinner size={"sm"} />
      ) : (
        <>
          {!isStarted && accumulatingTime === 0 && stopComponent
            ? stopComponent
            : null}
        </>
      )}
    </div>
  );
};
